import { useEffect, useState } from 'react';
import type { MultiComboboxOption } from '@/components/ui/MultiCombobox';
import { ApiError, api } from '@/lib/api';
import type { User, UserGroup, UserGroupSummary } from '@/lib/types';

/**
 * User groups: who may be NAMED as an account lead or a client success manager.
 *
 * ⚠️ **This is server-side ENFORCEMENT, not a convention.** Naming somebody who
 * is not in the group as `accountLeadId` or `clientSuccessManagerId` is a **422
 * naming the field, on `/companies` AND `/contacts`, create and update alike**.
 * So a narrowed picker states a real rule, and the copy around it may say so
 * plainly rather than hedging.
 *
 * ⚠️ **Never match on a group's NAME.** The embedded summary on a user carries
 * `{id, name}` and deliberately no `slug`, because a name is admin-editable free
 * text — the same shape that has burned this app three times (`includes('paid')`
 * reading "Unpaid" as settled, `includes('accept')` painting "Not Accepted"
 * green, a product rename switching campaign inclusions off). The machine key is
 * `slug`, and it exists on the `/user-groups` master alone, where it is
 * write-on-create-only and therefore cannot drift under an admin edit. That is
 * why resolving a group costs a second request: **write ids, render names, match
 * only the master's slug.**
 *
 * ⚠️ **Day one is one name per picker.** Measured in production: `account_lead`
 * 1 member, `client_success_manager` 1, `delivery_lead` 0. A one-name dropdown
 * reads as broken, so every narrowed picker says why it is narrow.
 *
 * ⚠️ **`delivery_lead` enforces nothing anywhere.** Its membership is editable
 * and no picker narrows to it. In particular a deliverable's `accountOwnerId` is
 * NOT narrowed: that is the deliverable's own owner answering a different
 * question, and narrowing it would drop stored owners off existing records to
 * satisfy a group that governs a different field.
 *
 * ⚠️ **`approvers` is not about naming anybody at all** — it is the RECIPIENT
 * LIST for company-approval emails. It refuses no write, narrows no picker, and
 * has no field it could refuse one on. It is here so the membership editor
 * offers it, which is the only way somebody gets put in it; while it is empty a
 * school is created `pending` and **nobody is emailed a link**, with a 2xx and
 * nothing on screen saying so.
 */

/**
 * The slugs, spelled once.
 *
 * These are the master's `slug` values — machine keys, fixed at creation (a
 * PATCH naming `slug` is a 422) — and they are the ONLY strings in this app that
 * may be compared against a group.
 *
 * ⚠️ **Listing a slug here does NOT narrow any picker.** Narrowing is opt-in at
 * a call site (`byGroup[slug]`), and only `account_lead` and
 * `client_success_manager` are enforced server-side. Two of these four govern no
 * write field at all.
 */
export const USER_GROUP_SLUGS = {
  accountLead: 'account_lead',
  clientSuccessManager: 'client_success_manager',
  deliveryLead: 'delivery_lead',
  /**
   * Who gets emailed a company-approval link.
   *
   * ⚠️ **A RECIPIENT LIST, NOT A GUARD.** It refuses no write anywhere — there
   * is no field to name somebody on — so nothing here narrows to it and nothing
   * should. What it decides is who `POST /companies` mails when a school needs
   * signing off.
   *
   * ⚠️ **An EMPTY approvers group fails SILENTLY and in the reassuring
   * direction**: the company is created `pending`, the request is recorded, and
   * **nobody is emailed**. The backend logs it and answers success, because the
   * company really was created. So this slug being listed — and therefore
   * offered by the membership editor on `/users` — is the only way anybody gets
   * put in the group, and until somebody is, every approval sits waiting on a
   * link that was never sent.
   */
  approvers: 'approvers',
} as const;

export type UserGroupSlug = (typeof USER_GROUP_SLUGS)[keyof typeof USER_GROUP_SLUGS];

/**
 * Every slug, in the order the membership editor offers them.
 *
 * ⚠️ **This list drives `useUserOptions`' per-slug narrowings, not the editor's
 * options.** The editor renders whatever `GET /user-groups` returns, so a group
 * added server-side appears there on its own; this list is what gives a call
 * site a `byGroup.<slug>` view to narrow a picker with. Nothing counts it.
 */
export const USER_GROUP_SLUG_LIST: readonly UserGroupSlug[] = [
  USER_GROUP_SLUGS.accountLead,
  USER_GROUP_SLUGS.clientSuccessManager,
  USER_GROUP_SLUGS.deliveryLead,
  USER_GROUP_SLUGS.approvers,
];

/**
 * A user's groups, read defensively.
 *
 * The contract says the key is **always present and `[]` when they are in
 * none** — which is why `User.groups` is typed as required. This exists for the
 * one state the type cannot describe: a response from a server that predates the
 * field, or a rollback. `user.groups.map(...)` would throw and take the page
 * down; `[]` renders the same as "in no group", which is the honest reading of a
 * response that does not mention them.
 *
 * Same shape as `linkOf()` in `src/lib/xero.ts`, and for the same reason: the
 * record type stays true to the contract and the helper absorbs the gap.
 */
export function groupsOf(user: Pick<User, 'groups'>): UserGroupSummary[] {
  const groups = (user as { groups?: UserGroupSummary[] }).groups;
  return Array.isArray(groups) ? groups : [];
}

/** Stable empty list, so a caller's `useMemo` deps do not churn. */
const NO_GROUPS: UserGroup[] = [];

export interface UserGroupsState {
  /** ACTIVE groups, in the master's own order. `[]` until they arrive. */
  records: UserGroup[];
  loading: boolean;
  /**
   * The read rejected for a reason other than 404, so this is "we do not know
   * what the groups are" — never "there are none".
   */
  failed: boolean;
  /**
   * The endpoint answered **404**: user groups are not set up on this API yet.
   *
   * Kept separate from `failed` deliberately, and kept at all even though the
   * backend has shipped: this app deploys independently of theirs, and a
   * surface that reads as broken between two deploys is a support call. When
   * this is true nothing narrows and every picker offers the whole directory —
   * which is exactly right, because a server with no groups enforces nothing.
   */
  unavailable: boolean;
}

/**
 * Reads the `/user-groups` master.
 *
 * `isActive: true` is sent explicitly: the filter is opt-in with **no default**,
 * like every other reference master here, so omitting it offers retired groups.
 *
 * ⚠️ **UNPAGINATED** — `{"data": [...]}` with no `meta`. There is no page to
 * ask for and no `hasMore` to report.
 */
export function useUserGroups(enabled = true): UserGroupsState {
  const [records, setRecords] = useState<UserGroup[]>(NO_GROUPS);
  // Seeded from `enabled` so the first paint shows a loading control rather than
  // an empty one, and nothing calls setState synchronously in an effect.
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listUserGroups({ isActive: true }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setRecords(response.data);
        setFailed(false);
        setUnavailable(false);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setRecords(NO_GROUPS);
        const notBuilt = caught instanceof ApiError && caught.status === 404;
        setUnavailable(notBuilt);
        setFailed(!notBuilt);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  return { records, loading: enabled ? loading : false, failed, unavailable };
}

/**
 * The master row for one slug, or `null` while it is unknown.
 *
 * **The slug is matched exactly**, with no case folding and no containment —
 * `slug` is a machine key, not prose, so a fuzzy match here would buy nothing
 * and would be the first step back towards matching names.
 */
export function groupBySlug(
  records: readonly UserGroup[],
  slug: UserGroupSlug,
): UserGroup | null {
  return records.find((record) => record.slug === slug) ?? null;
}

/** Whether this account is in the group with that id. */
export function isMemberOfGroup(user: Pick<User, 'groups'>, groupId: number): boolean {
  return groupsOf(user).some((group) => group.id === groupId);
}

/**
 * One picker's view of the staff directory.
 *
 * `narrowed` is the load-bearing field: it is the difference between "these are
 * the people who may hold this role" and "we could not work out who may, so
 * here is everybody". Only the first justifies telling a reader that somebody
 * already on the record is outside the group.
 */
export interface StaffNarrowing {
  /** The people this picker may offer. Every user when `narrowed` is false. */
  users: User[];
  /** True only when the group resolved and the list was really filtered. */
  narrowed: boolean;
  /** The group, for copy. Null whenever `narrowed` is false. */
  group: UserGroup | null;
}

/**
 * Narrows the directory to one group's members.
 *
 * ⚠️ **When the group cannot be resolved, nothing is narrowed and `narrowed` is
 * false.** That is the deliberate degradation for a 404, a failed master read,
 * or a slug the master does not carry: an unreadable master is not evidence that
 * nobody may lead an account, and a picker emptied on that basis would be a
 * dead end on every company in the CRM.
 *
 * The filtering is client-side over the page `useUserOptions` already holds,
 * rather than a second `GET /users?groupId=`, so a form still costs one staff
 * request however many narrowed pickers it mounts. The cost is the same one that
 * hook already reports: a member sitting past the first 100 users would be
 * missing, and `hasMore` is what says so.
 */
export function narrowStaffToGroup(
  users: User[],
  records: readonly UserGroup[],
  slug: UserGroupSlug,
): StaffNarrowing {
  const group = groupBySlug(records, slug);
  if (!group) return { users, narrowed: false, group: null };
  return {
    users: users.filter((user) => isMemberOfGroup(user, group.id)),
    narrowed: true,
    group,
  };
}

/** A narrowing that narrows nothing — the value every un-narrowed picker gets. */
export function openStaff(users: User[]): StaffNarrowing {
  return { users, narrowed: false, group: null };
}

/**
 * Should a stored lead the group does not contain be MARKED, and against which
 * group? `null` means **say nothing**.
 *
 * ⚠️ **Suppressing it while the list is unknown is the whole point.** An empty
 * `narrowing.users` proves nothing: the directory may still be loading, the
 * `/users` read may have failed, or the group may genuinely have no members. In
 * every one of those states the marker would accuse a perfectly valid account
 * lead of being outside the group, on every company in the CRM at once. Same
 * rule, and the same reasoning, as `regionOptions` in
 * `src/components/companies/company-master-options.ts`, which gates its own
 * marker on `records.length > 0`.
 *
 * The person is still OFFERED either way — dropping a stored value from a picker
 * is how a record gets silently reassigned by the next unrelated save. Only the
 * label waits for evidence.
 */
export function outsideGroupMarker(narrowing: StaffNarrowing): UserGroup | null {
  if (!narrowing.narrowed || !narrowing.group) return null;
  if (narrowing.users.length === 0) return null;
  return narrowing.group;
}

/**
 * That same decision, worded for a picker with a hint line (`Combobox`,
 * `InlineField`), or `null` meaning say nothing.
 *
 * The DECISION lives in `outsideGroupMarker` and the wording here, for the
 * reason `facultyOptions` gives about `retired`: a native `<option>` has no hint
 * slot, so `CompaniesPage`'s inline `<select>` has to spell the same fact more
 * briefly inside the option's own label. One decision, two renderings — never
 * two decisions.
 */
export function outsideGroupNote(narrowing: StaffNarrowing): string | null {
  const group = outsideGroupMarker(narrowing);
  return group ? `Not in the ${group.name} group — kept as it is` : null;
}

/**
 * What a narrowed picker says about itself, or `null` when there is nothing to
 * say.
 *
 * Day one this reads "Only the 1 person in Account lead can be named here" —
 * which is the sentence that stops a one-name dropdown reading as a broken one.
 */
export function narrowingNote(narrowing: StaffNarrowing): string | null {
  if (!narrowing.narrowed || !narrowing.group) return null;
  const count = narrowing.users.length;
  return count === 1
    ? `Only the 1 person in ${narrowing.group.name} can be named here. Add more on their user record.`
    : `Only the ${count} people in ${narrowing.group.name} can be named here. Add more on their user record.`;
}

/**
 * The membership editor's options: every ACTIVE group, plus any the account
 * already holds that the master no longer offers.
 *
 * ⚠️ **Injecting the held-but-unlisted ones is what makes silent data loss
 * unreachable**, exactly as it is for `regionIds` on a company: `groupIds` is a
 * whole-set REPLACE, so the array the editor assembles is the array the account
 * ends up with. Without a chip to sit on, a retired membership would be dropped
 * by the next save of an unrelated field — a 200, with nothing on screen having
 * said so.
 *
 * The marker is gated on `records.length > 0` for the reason `outsideGroupNote`
 * gives: an empty master is "not read yet", not "retired".
 */
export function groupMembershipOptions(
  records: readonly UserGroup[],
  stored: readonly UserGroupSummary[],
): MultiComboboxOption[] {
  const active: MultiComboboxOption[] = records.map((record) => ({
    value: String(record.id),
    label: record.name.trim() === '' ? `#${record.id}` : record.name.trim(),
  }));
  const known = new Set(active.map((option) => option.value));

  const held: MultiComboboxOption[] = [];
  for (const group of stored) {
    const value = String(group.id);
    if (known.has(value)) continue;
    known.add(value);
    held.push({
      value,
      label: group.name.trim() === '' ? `#${group.id}` : group.name.trim(),
      ...(records.length > 0 ? { hint: 'No longer offered — remove it to save this field' } : {}),
    });
  }

  return [...held, ...active];
}

/**
 * The membership ids to SEND, or `null` when there is nothing to send.
 *
 * ⚠️ **This is the structural half of the `[]`-versus-absent rule, and it is the
 * only thing in the form that may produce a `groupIds` value.** `[]` CLEARS
 * every membership and an absent key LEAVES IT ALONE — one falsy value carrying
 * two opposite instructions — so "unchanged" is returned as `null`, a value a
 * payload cannot express. A caller writes:
 *
 * ```ts
 * const ids = changedGroupIds(form.groupIds, baseline.groupIds);
 * if (ids !== null) body.groupIds = ids;
 * ```
 *
 * and there is no other assignment to the key. An empty array can therefore only
 * be reached by somebody removing the last chip from a set that had one, which
 * is the one case where clearing is what they asked for.
 *
 * Order is not a change: the server returns membership in its own order, so
 * comparing sets rather than arrays keeps a reordered draft out of the payload.
 */
export function changedGroupIds(
  next: readonly string[],
  baseline: readonly string[],
): number[] | null {
  const nextIds = uniqueIds(next);
  const baseIds = uniqueIds(baseline);
  if (nextIds.length === baseIds.length && nextIds.every((id, i) => id === baseIds[i])) {
    return null;
  }
  return nextIds;
}

/** Ids as a sorted, de-duplicated list, so two sets compare by value. */
function uniqueIds(values: readonly string[]): number[] {
  const ids = new Set<number>();
  for (const value of values) {
    const id = Number(value);
    if (Number.isInteger(id) && id > 0) ids.add(id);
  }
  return [...ids].sort((a, b) => a - b);
}

/**
 * Which of the ids just sent the server named in its refusal.
 *
 * The 422 lists EVERY offender in one message — "Unknown or inactive user
 * group(s): 4, 9" — precisely so all the bad chips can be marked at once rather
 * than the user resubmitting to discover the next one. Intersecting with what
 * was actually sent stops a stray number in the prose marking a chip that was
 * never in question.
 *
 * Same shape as `refusedRegionIds`, deliberately: it is the same server habit.
 */
export function refusedGroupIds(caught: ApiError, sent: readonly string[]): string[] {
  const text = (caught.details ?? [])
    .filter((detail) => detail.path === '' || detail.path.startsWith('groupIds'))
    .map((detail) => detail.message)
    .join(' ');
  const named = new Set(text.match(/\d+/g) ?? []);
  return sent.filter((value) => named.has(value));
}
